import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  ClipboardCopy,
  ExternalLink,
  Loader2,
  Save,
  Undo2,
  UploadCloud,
} from "lucide-react";
import { MarkdownEditor } from "@/components/MarkdownEditor.lazy";
import {
  getTtpaPageById,
  listTtpaClientOptions,
  publishTtpaPage,
  unpublishTtpaPage,
  updateTtpaPage,
  updateTtpaPageFinance,
  changeTtpaPageSlug,
  getTtpaPreviousVersion,
  type TtpaPage,
} from "@/lib/ttpa.functions";
import { normalizeTtpaSlug } from "@/lib/ttpa-slug";
import { TTPA_FUNDING_SOURCE_OPTIONS } from "@/lib/ttpa-template";
import { toast } from "sonner";
import { copyTextToClipboard, ttpaCopyToastMessage } from "@/lib/ttpa-copy";
import { TtpaCopyFallbackDialog } from "@/components/admin/TtpaCopyFallbackDialog";
import { TtpaOrphanNotice } from "@/components/admin/TtpaOrphanNotice";
import { confirmDialog } from "@/lib/confirm-dialog";



const dateFmt = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export const Route = createFileRoute("/_admin/admin/ttpa/$id")({
  component: TtpaEditorPage,
});


function TtpaEditorPage() {
  const { id } = Route.useParams();
  const getFn = useServerFn(getTtpaPageById);
  const updateFn = useServerFn(updateTtpaPage);
  const publishFn = useServerFn(publishTtpaPage);
  const unpublishFn = useServerFn(unpublishTtpaPage);
  const clientsFn = useServerFn(listTtpaClientOptions);
  const changeSlugFn = useServerFn(changeTtpaPageSlug);
  const previousFn = useServerFn(getTtpaPreviousVersion);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: page, isLoading, error } = useQuery({
    queryKey: ["admin-ttpa-page", id],
    queryFn: () => getFn({ data: { id } }),
  });

  const { data: clients } = useQuery({
    queryKey: ["admin-ttpa-clients"],
    queryFn: () => clientsFn(),
  });

  const [draft, setDraft] = useState<TtpaPage | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [copyFallback, setCopyFallback] = useState<string | null>(null);
  // Ticket T3a: Adressänderung ist eine bewusste manuelle Aktion.
  const [slugDialogOpen, setSlugDialogOpen] = useState(false);
  const [slugInput, setSlugInput] = useState("");
  const [slugError, setSlugError] = useState<string | null>(null);
  const [previousVersion, setPreviousVersion] = useState<string | null>(null);

  useEffect(() => {
    if (page) setDraft(page);
  }, [page]);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["admin-ttpa-pages"] });
    qc.invalidateQueries({ queryKey: ["admin-ttpa-page", id] });
    qc.invalidateQueries({ queryKey: ["ttpa-page"] });
    qc.invalidateQueries({ queryKey: ["client-ttpa-pages"] });
  }

  const saveMutation = useMutation({
    mutationFn: (input: {
      id: string;
      title: string;
      content_md: string;
      client_id: string | null;
    }) => updateFn({ data: input }),
    onSuccess: invalidate,
  });

  const publishMutation = useMutation({
    mutationFn: () => publishFn({ data: { id } }),
    onSuccess: invalidate,
  });

  const slugMutation = useMutation({
    mutationFn: (slug: string) => changeSlugFn({ data: { id, slug } }),
    onSuccess: invalidate,
  });

  const unpublishMutation = useMutation({
    mutationFn: () => unpublishFn({ data: { id } }),
    onSuccess: invalidate,
  });

  async function onSave() {
    if (!draft) return;
    setSaveError(null);
    try {
      await saveMutation.mutateAsync({
        id: draft.id,
        title: draft.title.trim(),
        content_md: draft.content_md ?? "",
        client_id: draft.client_id,
      });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onPublish() {
    if (!draft) return;
    setSaveError(null);
    try {
      await onSave();
      await publishMutation.mutateAsync();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onUnpublish() {
    setSaveError(null);
    if (
      !(await confirmDialog({
        title: "Seite auf Entwurf zurückziehen?",
        description: "Der öffentliche Link liefert danach nichts mehr.",
        confirmLabel: "Zurückziehen",
      }))
    ) {
      return;
    }
    try {
      await unpublishMutation.mutateAsync();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onChangeSlug() {
    setSlugError(null);
    const next = normalizeTtpaSlug(slugInput);
    if (!next) {
      setSlugError("Bitte eine gültige Adresse angeben.");
      return;
    }
    if (
      !(await confirmDialog({
        title: `Adresse auf /ttpa/${next} ändern?`,
        description:
          "Bestehende externe Verweise auf die alte Adresse funktionieren danach nicht mehr. Die Änderung wird protokolliert.",
        confirmLabel: "Adresse ändern",
      }))
    ) {
      return;
    }
    try {
      const res = await slugMutation.mutateAsync(next);
      setDraft((d) => (d ? { ...d, slug: res.slug } : d));
      setSlugDialogOpen(false);
    } catch (e) {
      setSlugError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onShowPrevious() {
    try {
      const res = await previousFn({ data: { id } });
      setPreviousVersion(res.contentMd ?? "Keine Vorversion gespeichert.");
    } catch (e) {
      setPreviousVersion(e instanceof Error ? e.message : String(e));
    }
  }

  // Ticket T1/T6: Markdown-Export – auch im Entwurfsstadium, damit die
  // Transparenzseite auf der Website des Kunden veröffentlicht werden kann.
  async function onCopyMarkdown() {
    if (!draft) return;
    const text = draft.content_md ?? "";
    const ok = await copyTextToClipboard(text);
    if (ok) {
      toast.success(ttpaCopyToastMessage(page?.has_unpublished_changes || !savedClean));
    } else {
      setCopyFallback(text);
    }
  }


  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Laden …
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="max-w-xl">
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "Seite nicht gefunden."}
        </p>
        <Link
          to="/admin/ttpa"
          className="mt-4 inline-flex items-center gap-1 text-sm font-semibold underline"
        >
          <ArrowLeft className="h-4 w-4" /> Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  if (!draft) return null;

  const savedClean =
    draft.title === page.title &&
    draft.content_md === page.content_md &&
    draft.client_id === page.client_id;

  const isPublished = page.status === "published";
  const needsRelease = page.has_unpublished_changes || !savedClean;
  const busy =
    saveMutation.isPending || publishMutation.isPending || unpublishMutation.isPending;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <button
            onClick={() => navigate({ to: "/admin/ttpa" })}
            className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Zurück
          </button>
          <h1 className="mt-2 font-display text-3xl font-extrabold md:text-4xl">
            TTPA-Seite bearbeiten
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span
              className={
                "rounded px-2 py-0.5 " +
                (isPublished
                  ? "bg-brand-wahlen/20 text-foreground"
                  : "bg-muted text-muted-foreground")
              }
            >
              {isPublished ? "Veröffentlicht" : "Entwurf"}
            </span>
            {isPublished && needsRelease && (
              <span className="rounded bg-brand-accent/15 px-2 py-0.5 text-foreground">
                Nicht freigegebene Änderungen
              </span>
            )}
            <span className="font-mono text-muted-foreground">
              {isPublished && page.published_slug
                ? `/ttpa/${page.published_slug}`
                : `Entwurfs-Slug: ${draft.slug}`}
            </span>
          </div>
          {page.auto_generated_at && (
            <div className="mt-3 max-w-2xl rounded-md border border-border bg-secondary/60 px-3 py-2 text-xs text-muted-foreground">
              <p>
                Automatisch erzeugt aus dem TTPA-Fragebogen vom{" "}
                {dateFmt.format(new Date(page.auto_generated_at))}. Änderungen hier
                betreffen nur den Entwurf – veröffentlicht wird per Hand.
              </p>
              {page.auto_overwrote_manual_at && (
                <p className="mt-2 text-foreground">
                  Achtung: Bei dieser Erzeugung wurden manuelle Änderungen am
                  Entwurfstext überschrieben. Bitte den neuen Entwurf gegen die
                  vorherige Fassung prüfen.
                  {page.has_previous_version && (
                    <button
                      onClick={onShowPrevious}
                      className="ml-1 font-semibold underline"
                    >
                      Vorherige Fassung anzeigen
                    </button>
                  )}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={onCopyMarkdown}
            className="inline-flex items-center gap-1 rounded border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary"
          >
            <ClipboardCopy className="h-3.5 w-3.5" /> Markdown kopieren
          </button>
          {isPublished && page.published_slug && (
            <a
              href={`/ttpa/${page.published_slug}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Ansehen
            </a>
          )}
          {isPublished && (
            <button
              onClick={onUnpublish}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
            >
              <Undo2 className="h-3.5 w-3.5" /> Auf Entwurf zurückziehen
            </button>
          )}
          <button
            onClick={onSave}
            disabled={busy || savedClean}
            className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold disabled:opacity-60"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Speichern
          </button>
          <button
            onClick={onPublish}
            disabled={busy || (isPublished && !needsRelease)}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
          >
            {publishMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <UploadCloud className="h-4 w-4" />
            )}
            {isPublished ? "Änderungen freigeben" : "Veröffentlichen"}
          </button>
        </div>
      </div>

      {copyFallback != null && (
        <TtpaCopyFallbackDialog text={copyFallback} onClose={() => setCopyFallback(null)} />
      )}


      {page.is_orphaned && draft.client_id == null && (
        <div className="mt-6 max-w-3xl">
          <TtpaOrphanNotice
            clientName={page.client_unassigned_client_name}
            unassignedAt={page.client_unassigned_at}
            isPublished={isPublished}
          />
        </div>
      )}

      <div className="mt-8 space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Titel
            </span>
            <input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
            />
          </label>
          <div className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Adresse (URL)
            </span>
            <div className="flex items-center gap-2">
              <input
                value={draft.slug}
                readOnly
                className="w-full rounded-md border border-input bg-muted p-2 font-mono text-sm text-muted-foreground"
              />
              <button
                onClick={() => {
                  setSlugInput(draft.slug);
                  setSlugError(null);
                  setSlugDialogOpen(true);
                }}
                className="shrink-0 rounded border border-border px-3 py-2 text-xs font-semibold hover:bg-secondary"
              >
                Ändern
              </button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Die Adresse wird beim Anlegen festgelegt und bleibt bei erneutem
              Fragebogen-Abschluss unverändert.
            </p>
          </div>
        </div>

        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Kunde (optional)
          </span>
          <select
            value={draft.client_id ?? ""}
            onChange={(e) => setDraft({ ...draft, client_id: e.target.value || null })}
            className="w-full rounded-md border border-input bg-background p-2 text-sm md:w-96"
          >
            <option value="">— keine Zuordnung —</option>
            {(clients ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.organization_name}
                {c.client_number ? ` (${c.client_number})` : ""}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">
            Die Zuordnung erscheint auf der Kunden-Detailseite und ist Grundlage für die
            spätere Befüllung aus dem TTPA-Fragebogen.
          </p>
        </label>

        <TtpaFinanceSection page={page} onUpdated={invalidate} />


        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Inhalt (Markdown)
          </span>
          <MarkdownEditor
            value={draft.content_md ?? ""}
            onChange={(v) => setDraft({ ...draft, content_md: v })}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Speichern ändert nur den Arbeitsstand. Öffentlich sichtbar wird der Inhalt erst
            mit „{isPublished ? "Änderungen freigeben" : "Veröffentlichen"}“. Suchmaschinen
            indexieren die Seiten nicht.
          </p>
        </label>
      </div>

      {slugDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4">
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-lg">
            <h2 className="font-display text-lg font-extrabold">Adresse ändern</h2>
            <p className="mt-2 text-xs text-muted-foreground">
              Bestehende externe Verweise auf die alte Adresse funktionieren danach
              nicht mehr. Die Änderung wird protokolliert.
            </p>
            <input
              value={slugInput}
              onChange={(e) => setSlugInput(e.target.value)}
              className="mt-4 w-full rounded-md border border-input bg-background p-2 font-mono text-sm"
            />
            <p className="mt-1 font-mono text-xs text-muted-foreground">
              /ttpa/{normalizeTtpaSlug(slugInput) || "…"}
            </p>
            {slugError && (
              <p className="mt-2 text-xs text-destructive">{slugError}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setSlugDialogOpen(false)}
                className="rounded border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary"
              >
                Abbrechen
              </button>
              <button
                onClick={onChangeSlug}
                disabled={slugMutation.isPending}
                className="rounded bg-brand-button px-3 py-1.5 text-xs font-semibold text-brand-button-foreground disabled:opacity-60"
              >
                Adresse ändern
              </button>
            </div>
          </div>
        </div>
      )}

      {previousVersion !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4">
          <div className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-lg border border-border bg-card p-5 shadow-lg">
            <h2 className="font-display text-lg font-extrabold">Vorherige Fassung</h2>
            <pre className="mt-3 flex-1 overflow-auto whitespace-pre-wrap rounded border border-border bg-secondary/60 p-3 text-xs">
              {previousVersion}
            </pre>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setPreviousVersion(null)}
                className="rounded border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {saveError && (
        <p className="mt-4 rounded border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
          {saveError}
        </p>
      )}

      <div className="mt-8 flex justify-end gap-2">
        <button
          onClick={onSave}
          disabled={busy || savedClean}
          className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold disabled:opacity-60"
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Speichern
        </button>
        <button
          onClick={onPublish}
          disabled={busy || (isPublished && !needsRelease)}
          className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
        >
          {publishMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <UploadCloud className="h-4 w-4" />
          )}
          {isPublished ? "Änderungen freigeben" : "Veröffentlichen"}
        </button>
      </div>
    </div>
  );
}

/**
 * Ticket T5: Strukturierte Finanzangaben (Datenbasis für die spätere
 * easyTTPA-Übergabe). Speichern setzt ein neues Stand-Datum, erneuert nur den
 * Finanzabschnitt im Entwurf und wird protokolliert.
 */
function TtpaFinanceSection({
  page,
  onUpdated,
}: {
  page: TtpaPage;
  onUpdated: () => void;
}) {
  const saveFinanceFn = useServerFn(updateTtpaPageFinance);
  const knownLabels = TTPA_FUNDING_SOURCE_OPTIONS.map((o) => o.label);

  const initialCost =
    page.estimated_campaign_cost_eur == null ? "" : String(page.estimated_campaign_cost_eur);
  const initialSelected = page.funding_sources.filter((s) => knownLabels.includes(s));
  const initialOther = page.funding_sources.find((s) => !knownLabels.includes(s)) ?? "";

  const [cost, setCost] = useState(initialCost);
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [other, setOther] = useState(initialOther);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [standAt, setStandAt] = useState<string | null>(page.finance_updated_at);

  const isEmpty =
    page.estimated_campaign_cost_eur == null && page.funding_sources.length === 0;

  const mutation = useMutation({
    mutationFn: (input: {
      estimated_campaign_cost_eur: number | null;
      funding_sources: string[];
    }) =>
      saveFinanceFn({ data: { id: page.id, ...input } }) as Promise<{
        ok: true;
        financeUpdatedAt: string;
      }>,

    onSuccess: (res) => {
      setStandAt(res.financeUpdatedAt);
      setMessage("Finanzangaben gespeichert. Der Finanzabschnitt im Entwurf wurde erneuert.");
      onUpdated();
    },
  });

  function toggle(label: string) {
    setSelected((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  }

  async function onSaveFinance() {
    setError(null);
    setMessage(null);
    const trimmed = cost.trim().replace(/[.\s]/g, "");
    let value: number | null = null;
    if (trimmed !== "") {
      if (!/^\d+$/.test(trimmed)) {
        setError("Bitte einen ganzzahligen Betrag in Euro angeben (ohne Komma).");
        return;
      }
      value = Number(trimmed);
      if (value > 100_000_000) {
        setError("Der Betrag ist unrealistisch hoch.");
        return;
      }
    }
    const sources = [...selected];
    if (other.trim() !== "") sources.push(`Sonstiges: ${other.trim()}`);
    try {
      await mutation.mutateAsync({
        estimated_campaign_cost_eur: value,
        funding_sources: sources,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="rounded-md border border-border bg-secondary/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Finanzangaben (strukturiert)
        </h2>
        {isEmpty ? (
          <span className="rounded bg-brand-accent/15 px-2 py-0.5 text-xs text-foreground">
            Finanzangaben fehlen noch
          </span>
        ) : (
          standAt && (
            <span className="text-xs text-muted-foreground">
              Stand der Finanzangaben: {dateFmt.format(new Date(standAt))}
            </span>
          )
        )}
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-foreground">
            Geschätzte Wahlkampfkosten (EUR)
          </span>
          <input
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            inputMode="numeric"
            placeholder="z. B. 25000"
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            {cost.trim() !== "" && /^\d+$/.test(cost.trim().replace(/[.\s]/g, ""))
              ? `${new Intl.NumberFormat("de-DE").format(
                  Number(cost.trim().replace(/[.\s]/g, "")),
                )} € (wird als Schätzung veröffentlicht)`
              : "Ganze Zahl in Euro. Wird auf der Transparenzseite als Schätzung gekennzeichnet."}
          </span>
        </label>

        <div className="block">
          <span className="mb-1 block text-xs font-semibold text-foreground">
            Finanzierungsquellen
          </span>
          <div className="space-y-1">
            {TTPA_FUNDING_SOURCE_OPTIONS.map((o) => (
              <label key={o.value} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selected.includes(o.label)}
                  onChange={() => toggle(o.label)}
                  className="h-4 w-4"
                />
                {o.label}
              </label>
            ))}
            <input
              value={other}
              onChange={(e) => setOther(e.target.value)}
              placeholder="Sonstiges (optional)"
              className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm"
            />
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          onClick={onSaveFinance}
          disabled={mutation.isPending}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
        >
          {mutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="h-3.5 w-3.5" />
          )}
          Finanzangaben speichern
        </button>
        <span className="text-xs text-muted-foreground">
          Aktualisiert nur den Finanzabschnitt im Entwurf. Adresse, Titel und der
          veröffentlichte Stand bleiben unverändert.
        </span>
      </div>

      {message && <p className="mt-3 text-xs text-foreground">{message}</p>}
      {error && <p className="mt-3 text-xs text-destructive">{error}</p>}
    </div>
  );
}

